import { useEffect, useRef, useState } from 'react';
import API_BASE_URL from '../config/api';

/**
 * Is this username free? Answered as the user types.
 *
 * Shared by signup and the profile page so both show the same wording — the
 * server owns the rules, and its `reason` string is displayed verbatim rather
 * than being re-implemented here. A second copy of "3-16 characters" in the
 * client is a second thing to forget to update.
 *
 * DEBOUNCED, because this fires on every keystroke and the endpoint has its own
 * rate limit. 400ms is long enough that typing a whole name costs one request.
 *
 * REQUESTS ARE ABORTED when superseded. Without that, typing `ada` then `adam`
 * can land the `ada` response last and show the wrong answer for the wrong
 * name — a race that only appears on a slow connection, which is exactly where
 * nobody is testing.
 */

const DEBOUNCE_MS = 400;

/**
 * @param {string} username
 * @param {string} [currentUsername] - treat this as free; it is already yours,
 *   which is what lets the profile page accept a change of casing.
 * @returns {{ status: 'idle'|'checking'|'available'|'unavailable', reason: string }}
 */
export default function useUsernameCheck(username, currentUsername = null) {
  const [state, setState] = useState({ status: 'idle', reason: '' });
  const abortRef = useRef(null);

  useEffect(() => {
    const trimmed = (username || '').trim();

    // Nothing typed yet, or it is the name they already have.
    if (!trimmed || trimmed.toLowerCase() === (currentUsername || '').toLowerCase()) {
      setState({ status: 'idle', reason: '' });
      return undefined;
    }

    setState({ status: 'checking', reason: '' });

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const url = `${API_BASE_URL}/api/auth/username-available?username=${encodeURIComponent(trimmed)}`;
        const response = await fetch(url, { signal: controller.signal });
        const data = await response.json();

        setState(data.available
          ? { status: 'available', reason: '' }
          : { status: 'unavailable', reason: data.reason || 'That username is not available.' });
      } catch (err) {
        // An abort is the expected path when someone keeps typing — it is not
        // a failure and must not overwrite the newer request's state.
        if (err.name === 'AbortError') return;
        setState({ status: 'idle', reason: '' });
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [username, currentUsername]);

  return state;
}
